import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/AuthContext";
import { NotificationsProvider } from "../hooks/NotificationsContext";
import { PreferencesProvider } from "../hooks/PreferencesContext";
import { ErrorBoundary } from "./ErrorBoundary";
import { Icon } from "./Icon";
import { ActivityRail } from "./shell/ActivityRail";
import { AccountMenu } from "./shell/AccountMenu";
import { WikiSearch } from "./shell/WikiSearch";

export function Layout() {
  return (
    <PreferencesProvider>
      <NotificationsProvider>
        <Shell />
      </NotificationsProvider>
    </PreferencesProvider>
  );
}
function Shell() {
  const { user, unavailable, refresh } = useAuth();
  const location = useLocation();
  const navTrigger = useRef<HTMLButtonElement>(null);
  const [navOpen, setNavOpen] = useState(false);
  const root = location.pathname.split("/")[1] ?? "";
  const repositoryPage =
    location.pathname.split("/").length > 2 &&
    !["settings", "explore", "user"].includes(root);
  const authPage = ["/login", "/register"].includes(location.pathname);
  useEffect(() => {
    setNavOpen(false);
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.search, location.hash]);
  const next = authPage
    ? location.search
    : `?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`;
  return (
    <div
      className="library-app"
      onKeyDown={(event) => {
        if (event.key === "Escape" && navOpen) {
          setNavOpen(false);
          navTrigger.current?.focus();
        }
      }}
    >
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <ActivityRail
        signedIn={!!user}
        username={user?.username}
        open={navOpen}
        onClose={() => setNavOpen(false)}
      />
      {navOpen && (
        <button
          className="navigation-scrim"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      )}
      <div
        className={`workspace-content${repositoryPage ? " repository-view" : ""}`}
      >
        <header className="workspace-topbar">
          <button
            ref={navTrigger}
            className="btn ghost icon-btn navigation-toggle"
            aria-label="Open navigation"
            aria-expanded={navOpen}
            aria-controls="library-navigation"
            onClick={() => setNavOpen(!navOpen)}
          >
            <Icon name="menu" />
          </button>
          <Link to="/" className="library-wordmark">
            Athenaeum <span>Library</span>
          </Link>
          {!authPage && <WikiSearch />}
          <nav className="topbar-resources" aria-label="Resources">
            <Link to="/download">Download</Link>
            <Link to="/explore/guide">Documentation</Link>
          </nav>
          <div className="topbar-actions">
            {user ? (
              <AccountMenu key={location.pathname} />
            ) : (
              user !== undefined && (
                <>
                  <Link className="btn ghost sm" to={`/login${next}`}>
                    Sign in
                  </Link>
                  <Link className="btn primary sm" to={`/register${next}`}>
                    Sign up
                  </Link>
                </>
              )
            )}
          </div>
        </header>
        {unavailable && (
          <div className="alert warn offline-banner" role="alert">
            <span>
              <strong>The Library server cannot be reached.</strong> Your local
              wiki remains on your computer.
            </span>
            <button className="btn sm" onClick={() => void refresh()}>
              Try again
            </button>
          </div>
        )}
        <main
          id="main"
          className={`workspace-main${authPage ? " authentication-main" : ""}`}
          tabIndex={-1}
        >
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
